import { zoneOffsetMinutesAt } from '../../../components/payrollEdit/clockParse';
import { addDaysYmd, fmtYmdDow, localMinutes, localYmd, offsetAt } from './portalTime';
import { jobFromRow, sameJob, sourceKind, SourceKind } from './portalModel';
import type { PunchKind } from './portalModel';
import {
  PortalHourlyRow,
  PortalJob,
  PortalPayPeriod,
  PortalTimeSummary,
  PortalTimesheetStatus,
  PUNCH_TYPE_MANUAL_ADD,
  PUNCH_TYPE_MANUAL_EDIT,
  TYPE_MEAL,
  TYPE_REGULAR,
} from './portalTypes';

/**
 * Employee Time Portal — My Timesheet's pure model (P-TAS-20 Phase 3, App-2). Week math,
 * the status rail, per-day punches (with the row sides each punch lives on), the edit /
 * add-missing request bodies, the pay-period pre-check and the getTimeSummary totals.
 * No React, no axios, no config: the tenant zone and "today" are always arguments.
 *
 * A punch the employee sees is one SIDE of a TimeEntryHourly row — or two sides at once:
 * Meal Start is the REG row's out AND the MEAL row's in (the same instant), Meal End the
 * MEAL row's out and the next REG row's in, a job switch one REG row's out and the next
 * one's in. Editing such a punch moves both sides, in the order that never makes the two
 * rows overlap (the API refuses PUNCH_OVERLAP).
 */

/* ------------------------------------------------------------------ week math */

export const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DOW_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** 0 = Monday … 6 = Sunday for a calendar date. */
export function dowIndex(ymd: string): number {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`).getUTCDay();
  return (d + 6) % 7;
}

/** The 7 calendar dates of a pay period, from its start (Mon → Sun). */
export function weekYmds(period: Pick<PortalPayPeriod, 'startYmd'>): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysYmd(period.startYmd, i));
}

export function periodContaining(periods: PortalPayPeriod[], ymd: string): PortalPayPeriod | null {
  return periods.find((p) => p.startYmd <= ymd && ymd <= p.endYmd) ?? null;
}

/** Union of two period lists, de-duplicated by ID, sorted by start. */
export function mergePeriods(a: PortalPayPeriod[], b: PortalPayPeriod[]): PortalPayPeriod[] {
  const map = new Map<number, PortalPayPeriod>();
  for (const p of [...a, ...b]) map.set(p.payPeriodID, p);
  return Array.from(map.values()).sort((x, y) => (x.startYmd < y.startYmd ? -1 : x.startYmd > y.startYmd ? 1 : 0));
}

/** The week before / after `period` in the list, or null when it isn't loaded. */
export function adjacentPeriod(periods: PortalPayPeriod[], period: PortalPayPeriod, dir: -1 | 1): PortalPayPeriod | null {
  const sorted = mergePeriods(periods, []);
  const i = sorted.findIndex((p) => p.payPeriodID === period.payPeriodID);
  if (i < 0) return null;
  return sorted[i + dir] ?? null;
}

export type WeekPhase = 'past' | 'current' | 'future';

export function weekPhase(period: Pick<PortalPayPeriod, 'startYmd' | 'endYmd'>, todayYmd: string): WeekPhase {
  if (todayYmd > period.endYmd) return 'past';
  if (todayYmd < period.startYmd) return 'future';
  return 'current';
}

function monDay(ymd: string, withYear: boolean): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
}

/** `Jul 27 – Aug 2, 2026` (the stepper label). */
export function weekLabel(period: Pick<PortalPayPeriod, 'startYmd' | 'endYmd'>): string {
  return `${monDay(period.startYmd, false)} – ${monDay(period.endYmd, true)}`;
}

/* ------------------------------------------------------------------ wall time ↔ instant */

/**
 * A wall time on `ymd` (`minutes` after local midnight) in `zone` → epoch ms. Two passes
 * so the offset is the one in force at that wall time (DST-correct), as the payroll
 * editor's wallTimeToTenantInstant does, but for the portal's zone.
 */
export function wallToInstant(ymd: string, minutes: number, zone: string): number {
  const [y, mo, d] = ymd.split('-').map((x) => parseInt(x, 10));
  const wallUTC = Date.UTC(y, mo - 1, d, 0, minutes, 0);
  let off = zoneOffsetMinutesAt(wallUTC, zone);
  let instant = wallUTC - off * 60000;
  off = zoneOffsetMinutesAt(instant, zone);
  instant = wallUTC - off * 60000;
  return instant;
}

/** `HH:MM` (an `<input type="time">` value) → minutes after midnight, else null. */
export function parseTimeInput(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** An instant → the `HH:MM` value for a time input, in `zone`. */
export function toTimeInput(ms: number, zone: string): string {
  const mins = Math.floor(localMinutes(ms, zone));
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

/* ------------------------------------------------------------------ add missing time */

const DAY_MIN = 24 * 60;
const pad2 = (n: number) => String(n).padStart(2, '0');
/** Minutes after midnight → the time input's `HH:MM` (24:00 shows as 23:59). */
export function minutesToTimeInput(min: number): string {
  const m = Math.max(0, Math.min(DAY_MIN - 1, Math.round(min)));
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

/**
 * "Add missing time"'s starting range: a FREE gap on the day, so the first save isn't
 * refused as an overlap. Scans the day's busy time (rows, an open one running to now) in
 * order and takes the first gap of at least 1 h — before the first punch, between punches,
 * or after the last:
 *   - a gap that ends at a punch ends the range AT that punch (a forgotten clock-in), and
 *     starts it up to 4 h earlier (not before 6:00 AM unless the gap is shorter);
 *   - the gap after the last punch starts at its end and runs up to 4 h (on today, not past now).
 * Nothing recorded → 8:00 AM – 12:00 PM. No 1 h gap → last punch end + 1 h.
 */
export function defaultAddRange(
  dayRows: Pick<TsRow, 'inMs' | 'outMs'>[],
  ymd: string,
  zone: string,
  nowMs: number,
  todayYmd: string,
): { from: string; to: string } {
  const clamp = (ms: number) => {
    const d = localYmd(ms, zone);
    return d < ymd ? 0 : d > ymd ? DAY_MIN : localMinutes(ms, zone);
  };
  const busy = dayRows
    .map((r) => ({ a: clamp(r.inMs), b: clamp(r.outMs ?? Math.max(nowMs, r.inMs)) }))
    .sort((x, y) => x.a - y.a);
  if (!busy.length) return { from: '08:00', to: '12:00' };
  const limit = ymd === todayYmd ? Math.min(DAY_MIN, localMinutes(nowMs, zone)) : DAY_MIN;

  let cursor = 0;
  for (const iv of busy) {
    if (iv.a - cursor >= 60) {
      const to = iv.a;
      const from = Math.max(cursor, to - 240, Math.min(6 * 60, to - 60));
      return { from: minutesToTimeInput(from), to: minutesToTimeInput(to) };
    }
    cursor = Math.max(cursor, iv.b);
  }
  if (limit - cursor >= 60) {
    return { from: minutesToTimeInput(cursor), to: minutesToTimeInput(Math.min(limit, cursor + 240)) };
  }
  return { from: minutesToTimeInput(cursor), to: minutesToTimeInput(Math.min(DAY_MIN - 1, cursor + 60)) };
}

/** The first row a new [inMs, outMs) block would overlap (strict, touching passes — as the API), or null. */
export function findOverlap<T extends Pick<TsRow, 'inMs' | 'outMs'>>(rows: T[], inMs: number, outMs: number, nowMs: number): T | null {
  for (const r of rows) {
    const end = r.outMs ?? Math.max(nowMs, r.inMs);
    if (inMs < end && r.inMs < outMs) return r;
  }
  return null;
}

/** `7:45a` (the grid's compact time). */
export function shortTime(ms: number, zone: string): string {
  const t = new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: zone });
  return t.replace(/\s?AM$/i, 'a').replace(/\s?PM$/i, 'p');
}

/* ------------------------------------------------------------------ rows */

/** A side counts as Edited when it was added or changed by hand, or payroll touched the row. */
export function isSideEdited(row: PortalHourlyRow, side: 'in' | 'out'): boolean {
  const t = side === 'in' ? row.punchTypeIn : row.punchTypeOut;
  if (t === PUNCH_TYPE_MANUAL_ADD || t === PUNCH_TYPE_MANUAL_EDIT) return true;
  return (row.modifiedSource ?? '').toUpperCase() === 'PAYROLL' || (side === 'in' && (row.createdSource ?? '').toUpperCase() === 'PAYROLL');
}

export function isRowEdited(row: PortalHourlyRow): boolean {
  return isSideEdited(row, 'in') || (row.outDateTime != null && isSideEdited(row, 'out'));
}

export interface TsRow {
  id: number;
  payPeriodID: number;
  inMs: number;
  outMs: number | null;
  type: number;
  job: PortalJob;
  inSource: SourceKind;
  outSource: SourceKind | null;
  inEdited: boolean;
  outEdited: boolean;
  raw: PortalHourlyRow;
}

export function toTsRow(row: PortalHourlyRow): TsRow | null {
  const inMs = Date.parse(row.inDateTime);
  if (Number.isNaN(inMs)) return null;
  const outParsed = row.outDateTime ? Date.parse(row.outDateTime) : NaN;
  const outMs = Number.isNaN(outParsed) ? null : outParsed;
  return {
    id: row.timeEntryHourlyID,
    payPeriodID: row.payPeriodID,
    inMs,
    outMs,
    type: row.type,
    job: jobFromRow(row),
    inSource: sourceKind(row.createdSource),
    outSource: outMs == null ? null : sourceKind(row.modifiedSource ?? row.createdSource),
    inEdited: isSideEdited(row, 'in'),
    outEdited: outMs != null && isSideEdited(row, 'out'),
    raw: row,
  };
}

export function toTsRows(rows: PortalHourlyRow[]): TsRow[] {
  return rows
    .map(toTsRow)
    .filter((r): r is TsRow => r != null)
    .sort((a, b) => a.inMs - b.inMs || a.id - b.id);
}

/** Rows whose in-time falls on `ymd` in `zone`. */
export function rowsOnDay(rows: TsRow[], ymd: string, zone: string): TsRow[] {
  return rows.filter((r) => localYmd(r.inMs, zone) === ymd);
}

/* ------------------------------------------------------------------ the day's punches */

/** Two punches within this many ms are the same instant (a meal / switch closes and opens at once). */
const SAME_INSTANT_MS = 60 * 1000;

export type DayPunchKind = PunchKind | 'switch' | 'missingOut';

export const DAY_PUNCH_LABEL: Record<DayPunchKind, string> = {
  in: 'Clock In',
  out: 'Clock Out',
  'meal-start': 'Meal Start',
  'meal-end': 'Meal End',
  'meal2-start': '2nd Meal Start',
  'meal2-end': '2nd Meal End',
  switch: 'Job switch',
  missingOut: 'Clock Out',
};

export interface PunchSide {
  rowID: number;
  side: 'in' | 'out';
}

export interface DayPunch {
  key: string;
  kind: DayPunchKind;
  /** The punch time; for `missingOut`, the in-time of the open row it would close. */
  atMs: number;
  source: SourceKind;
  edited: boolean;
  /** Every row side that sits at this instant (1, or 2 for a meal / switch boundary). */
  sides: PunchSide[];
  /** A job switch: the job before and after. */
  from?: PortalJob;
  to?: PortalJob;
}

/**
 * The punches of one day, oldest first, each with the row sides it is made of. An open
 * row on a day before `todayYmd` (a forgotten clock out) ends in a `missingOut` entry; a
 * row still open today is the running shift and gets no out entry.
 */
export function dayPunches(allRows: TsRow[], ymd: string, zone: string, todayYmd: string): DayPunch[] {
  const rows = rowsOnDay(allRows, ymd, zone);
  const out: DayPunch[] = [];
  let mealIndex = 0;
  rows.forEach((r, i) => {
    const prev = i > 0 ? rows[i - 1] : null;
    const next = i < rows.length - 1 ? rows[i + 1] : null;
    const touchesPrev = !!prev && prev.outMs != null && Math.abs(prev.outMs - r.inMs) <= SAME_INSTANT_MS;
    const touchesNext = !!next && r.outMs != null && Math.abs(next.inMs - r.outMs) <= SAME_INSTANT_MS;

    if (r.type === TYPE_MEAL) {
      mealIndex += 1;
      const second = mealIndex >= 2;
      const startSides: PunchSide[] = [{ rowID: r.id, side: 'in' }];
      let startEdited = r.inEdited;
      if (touchesPrev && prev) {
        startSides.unshift({ rowID: prev.id, side: 'out' });
        startEdited = startEdited || prev.outEdited;
      }
      out.push({ key: `${r.id}-in`, kind: second ? 'meal2-start' : 'meal-start', atMs: r.inMs, source: r.inSource, edited: startEdited, sides: startSides });
      if (r.outMs != null) {
        const endSides: PunchSide[] = [{ rowID: r.id, side: 'out' }];
        let endEdited = r.outEdited;
        if (touchesNext && next) {
          endSides.push({ rowID: next.id, side: 'in' });
          endEdited = endEdited || next.inEdited;
        }
        out.push({ key: `${r.id}-out`, kind: second ? 'meal2-end' : 'meal-end', atMs: r.outMs, source: r.outSource ?? r.inSource, edited: endEdited, sides: endSides });
      } else if (ymd < todayYmd) {
        out.push({ key: `${r.id}-miss`, kind: 'missingOut', atMs: r.inMs, source: r.inSource, edited: false, sides: [{ rowID: r.id, side: 'out' }] });
      }
      return;
    }

    if (!touchesPrev) {
      out.push({ key: `${r.id}-in`, kind: 'in', atMs: r.inMs, source: r.inSource, edited: r.inEdited, sides: [{ rowID: r.id, side: 'in' }] });
    } else if (prev && prev.type !== TYPE_MEAL) {
      // REG → REG at the same instant: a job switch (or a same-job split, shown the same way
      // so both sides stay editable).
      out.push({
        key: `${r.id}-sw`, kind: 'switch', atMs: r.inMs, source: r.inSource, edited: r.inEdited || prev.outEdited,
        sides: [{ rowID: prev.id, side: 'out' }, { rowID: r.id, side: 'in' }], from: prev.job, to: r.job,
      });
    }
    // (A REG row right after a meal: its in is the Meal End above.)
    if (r.outMs != null && !touchesNext) {
      out.push({ key: `${r.id}-out`, kind: 'out', atMs: r.outMs, source: r.outSource ?? r.inSource, edited: r.outEdited, sides: [{ rowID: r.id, side: 'out' }] });
    } else if (r.outMs == null && ymd < todayYmd) {
      out.push({ key: `${r.id}-miss`, kind: 'missingOut', atMs: r.inMs, source: r.inSource, edited: false, sides: [{ rowID: r.id, side: 'out' }] });
    }
  });
  return out;
}

/** Worked (REG) and meal minutes of a day's rows; an open row counts up to `nowMs`. */
export function dayMinutes(allRows: TsRow[], ymd: string, zone: string, nowMs: number): { worked: number; meal: number } {
  let worked = 0;
  let meal = 0;
  for (const r of rowsOnDay(allRows, ymd, zone)) {
    const m = Math.max(0, ((r.outMs ?? Math.max(nowMs, r.inMs)) - r.inMs) / 60000);
    if (r.type === TYPE_MEAL) meal += m;
    else worked += m;
  }
  return { worked, meal };
}

/** The distinct jobs a day's REG rows are charged to, in order. */
export function dayJobs(allRows: TsRow[], ymd: string, zone: string): PortalJob[] {
  const jobs: PortalJob[] = [];
  for (const r of rowsOnDay(allRows, ymd, zone)) {
    if (r.type === TYPE_MEAL) continue;
    if (!jobs.some((j) => sameJob(j, r.job))) jobs.push(r.job);
  }
  return jobs;
}

/* ------------------------------------------------------------------ edit bodies */

export interface RowSideChange {
  /** New in-time (epoch ms); undefined = unchanged. */
  inMs?: number;
  /** New out-time; undefined = unchanged. */
  outMs?: number;
}

/**
 * The PUT body for a row with one or both sides moved: EVERY field is resent from the row
 * as the server holds it (PUT replaces the row; an omitted job would be cleared), the moved
 * side gets its new instant, its tenant-zone offset, and `punchType` (3 = edited; 2 when a
 * missing clock out is being added).
 */
export function buildRowEditBody(row: PortalHourlyRow, change: RowSideChange, zone: string, punchType: number = PUNCH_TYPE_MANUAL_EDIT): Record<string, unknown> {
  const inMs = change.inMs ?? Date.parse(row.inDateTime);
  const inChanged = change.inMs !== undefined;
  const outChanged = change.outMs !== undefined;
  const outMs = outChanged ? change.outMs! : row.outDateTime ? Date.parse(row.outDateTime) : null;
  const body: Record<string, unknown> = {
    inDateTime: new Date(inMs).toISOString(),
    inDateTimeOffset: inChanged ? offsetAt(inMs, zone) : row.inDateTimeOffset,
    outDateTime: outMs == null ? null : new Date(outMs).toISOString(),
    outDateTimeOffset: outMs == null ? null : outChanged ? offsetAt(outMs, zone) : row.outDateTimeOffset,
    type: row.type,
    punchTypeIn: inChanged ? punchType : row.punchTypeIn,
    punchTypeOut: outMs == null ? null : outChanged ? punchType : row.punchTypeOut,
    projectID: row.project?.projectID ?? null,
    accountCodeID: row.account?.accountCodeID ?? null,
    projectPhaseID: row.projectPhase?.projectPhaseID ?? null,
    serviceOrderText: row.serviceOrderText || null,
    customerServiceWarrantyID: row.customerServiceWarranty?.customerServiceWarrantyID || null,
    locationName: row.locationName || null,
    description: row.description || null,
    serviceOrderID: row.serviceOrder?.serviceOrderID ?? null,
    serviceOrderScopeID: row.serviceOrderScopeID ?? null,
    tempServiceOrderDescription: row.tempServiceOrderDescription ?? null,
  };
  if (row.inLatitude != null && row.inLongitude != null) {
    body.inLatitude = row.inLatitude;
    body.inLongitude = row.inLongitude;
  }
  if (row.outLatitude != null && row.outLongitude != null && outMs != null) {
    body.outLatitude = row.outLatitude;
    body.outLongitude = row.outLongitude;
  }
  return body;
}

/** POST body for time added by hand: a closed row (or an open one when `outMs` is null), punch type 2. */
export function buildManualRowBody(args: {
  employeeID: number;
  inMs: number;
  outMs: number | null;
  zone: string;
  type: number;
  job: PortalJob;
  /** Punch type of the out side; defaults to 2 (manual add). */
  punchTypeOut?: number | null;
}): Record<string, unknown> {
  const { inMs, outMs, zone, job } = args;
  return {
    employeeID: args.employeeID,
    inDateTime: new Date(inMs).toISOString(),
    inDateTimeOffset: offsetAt(inMs, zone),
    outDateTime: outMs == null ? null : new Date(outMs).toISOString(),
    outDateTimeOffset: outMs == null ? null : offsetAt(outMs, zone),
    type: args.type,
    punchTypeIn: PUNCH_TYPE_MANUAL_ADD,
    punchTypeOut: outMs == null ? null : args.punchTypeOut ?? PUNCH_TYPE_MANUAL_ADD,
    projectID: job.projectID ?? null,
    accountCodeID: job.accountCodeID ?? null,
    projectPhaseID: job.projectPhaseID ?? null,
    serviceOrderText: null,
    customerServiceWarrantyID: null,
    locationName: null,
    description: null,
    serviceOrderID: null,
    serviceOrderScopeID: null,
    tempServiceOrderDescription: null,
  };
}

/* ------------------------------------------------------------------ edit plans */

export interface PlannedPut {
  rowID: number;
  change: RowSideChange;
}

export type EditPlan = { ok: true; puts: PlannedPut[] } | { ok: false; reason: string };

export const OUT_BEFORE_IN_MESSAGE = 'The out time must be after the in time.';

/**
 * Moving a punch to `newMs`: one PUT per row side it is made of, ordered so the rows never
 * overlap in between — moving LATER shifts the next row's in first (opening a gap), then
 * the previous row's out; moving EARLIER the reverse. Refuses a move that would put a row's
 * out at or before its in, or carry a boundary past its neighbours.
 */
export function planPunchEdit(punch: DayPunch, newMs: number, rows: TsRow[]): EditPlan {
  const byID = new Map(rows.map((r) => [r.id, r]));
  const puts: PlannedPut[] = [];
  for (const s of punch.sides) {
    const r = byID.get(s.rowID);
    if (!r) return { ok: false, reason: "That punch wasn't found. Refresh and try again." };
    const inMs = s.side === 'in' ? newMs : r.inMs;
    const outMs = s.side === 'out' ? newMs : r.outMs;
    if (outMs != null && outMs <= inMs) return { ok: false, reason: OUT_BEFORE_IN_MESSAGE };
    puts.push({ rowID: r.id, change: s.side === 'in' ? { inMs: newMs } : { outMs: newMs } });
  }
  const later = newMs > punch.atMs;
  // later → 'in' sides (the following row) first; earlier → 'out' sides (the preceding row) first.
  puts.sort((a, b) => {
    const ai = a.change.inMs !== undefined ? 0 : 1;
    const bi = b.change.inMs !== undefined ? 0 : 1;
    return later ? ai - bi : bi - ai;
  });
  return { ok: true, puts };
}

/**
 * Adding a meal break [startMs, endMs] inside one REG row: shorten the row to end at the
 * meal start (PUT), add the MEAL row (POST), then add the rest of the shift on the same
 * job (POST; still open when the original row was). Null when no single REG row covers it.
 */
export function planMealInsert(rows: TsRow[], startMs: number, endMs: number): { row: TsRow } | { reason: string } {
  if (endMs <= startMs) return { reason: 'The meal must end after it starts.' };
  const host = rows.find((r) => r.type === TYPE_REGULAR && r.inMs < startMs && (r.outMs == null || r.outMs > endMs));
  if (!host) return { reason: 'A meal must fall inside worked time. Add the work time first, or edit the punches around it.' };
  return { row: host };
}

/* ------------------------------------------------------------------ pay period pre-check */

export type PeriodCheck =
  | { kind: 'same' }
  | { kind: 'moves'; targetYmd: string; target: PortalPayPeriod | null };

/**
 * Whether a row whose in-time moves to `newInMs` stays in its week. The API decides the
 * week by the in-time's LOCAL date (dbo.GetPayPeriodIDWithOffset), so this compares the
 * new local date against the row's week; a move is refused there with PAY_PERIOD_CHANGE.
 */
export function precheckPeriod(newInMs: number, zone: string, rowPeriod: Pick<PortalPayPeriod, 'startYmd' | 'endYmd'>, periods: PortalPayPeriod[]): PeriodCheck {
  const ymd = localYmd(newInMs, zone);
  if (ymd >= rowPeriod.startYmd && ymd <= rowPeriod.endYmd) return { kind: 'same' };
  return { kind: 'moves', targetYmd: ymd, target: periodContaining(periods, ymd) };
}

/* ------------------------------------------------------------------ move a block to another week */

/** The portal calls a week move needs (injected so the sequence is testable). */
export interface WeekMoveDeps {
  getStatus: (payPeriodID: number) => Promise<PortalTimesheetStatus>;
  add: (body: Record<string, unknown>) => Promise<{ timeEntryHourlyID?: number; timeEntriesHourly?: PortalHourlyRow[] }>;
  update: (timeEntryHourlyID: number, body: Record<string, unknown>) => Promise<void>;
  remove: (timeEntryHourlyID: number) => Promise<void>;
  newClientPunchID: () => string;
}

export interface WeekMoveArgs {
  employeeID: number;
  zone: string;
  /** The closed block whose IN moves into another week (its OUT stays). */
  row: TsRow;
  newInMs: number;
  target: PortalPayPeriod;
}

/** Thrown by {@link moveRowToOtherWeek}. `restored`: every write was undone; `partial`: some of it stuck. */
export class WeekMoveError extends Error {
  readonly cause: unknown;
  readonly restored: boolean;
  readonly partial: boolean;
  /** A refusal decided before any write (the target week is locked). */
  readonly refusedUpFront: boolean;
  constructor(message: string, opts: { cause?: unknown; restored?: boolean; partial?: boolean; refusedUpFront?: boolean }) {
    super(message);
    this.cause = opts.cause;
    this.restored = !!opts.restored;
    this.partial = !!opts.partial;
    this.refusedUpFront = !!opts.refusedUpFront;
  }
}

export function weekMoveLockMessage(status: PortalTimesheetStatus, target: PortalPayPeriod): string | null {
  const where = `the week of ${weekLabel(target)}`;
  if (status.exported) return `Payroll for ${where} was already exported, so the punch can't move there. Contact payroll to make changes.`;
  if (status.submitted || status.locked) {
    return `${where.charAt(0).toUpperCase()}${where.slice(1)} was already submitted, so the punch can't move there. Ask your supervisor or payroll to unlock that week.`;
  }
  return null;
}

/**
 * Moves a closed block's IN into another week (its OUT stays): a PUT can't change a row's
 * week (PAY_PERIOD_CHANGE), so the block is re-added there and the original deleted.
 *
 *   0. The TARGET week's status first: submitted / exported → refused, nothing written.
 *   Then the new row is POSTed BEFORE the original is deleted — but both end at the same
 *   OUT, so they would overlap (the portal POST refuses PUNCH_OVERLAP). Room is made first:
 *   - later (new IN after the old IN): 1. PUT the original's OUT back to the new IN (it
 *     still holds its own time), 2. POST [new IN, OUT] (touching, allowed), 3. DELETE the
 *     original.
 *   - earlier (new IN before the old IN): 1. POST [new IN, old IN] (touching), 2. DELETE the
 *     original, 3. PUT the new row's OUT out to the original OUT.
 * If the POST fails nothing was deleted (a shrunk original is put back). If a later step
 * fails, the new row is deleted and the original restored (best effort; `restored` says
 * whether it all came back).
 */
export async function moveRowToOtherWeek(deps: WeekMoveDeps, args: WeekMoveArgs): Promise<void> {
  const { employeeID, zone, row, newInMs, target } = args;
  if (row.outMs == null) throw new WeekMoveError('Only a block with a clock out can move to another week.', { refusedUpFront: true });
  const outMs = row.outMs;

  const status = await deps.getStatus(target.payPeriodID);
  const lockMsg = weekMoveLockMessage(status, target);
  if (lockMsg) throw new WeekMoveError(lockMsg, { refusedUpFront: true });

  const newBody = (toMs: number) => ({
    ...buildManualRowBody({ employeeID, inMs: newInMs, outMs: toMs, zone, type: row.type, job: row.job, punchTypeOut: row.raw.punchTypeOut }),
    clientPunchID: deps.newClientPunchID(),
  });
  const restoreOriginalOut = () => deps.update(row.id, buildRowEditBody(row.raw, { outMs }, zone, row.raw.punchTypeOut ?? PUNCH_TYPE_MANUAL_EDIT));
  /** Undo after the POST landed: delete the new row, then put the original back as it was. */
  const undo = async (newID: number | null, originalDeleted: boolean, originalShrunk: boolean): Promise<boolean> => {
    let ok = true;
    if (newID != null) {
      try { await deps.remove(newID); } catch { ok = false; }
    }
    if (originalDeleted) {
      try {
        await deps.add({
          ...buildManualRowBody({ employeeID, inMs: row.inMs, outMs, zone, type: row.type, job: row.job, punchTypeOut: row.raw.punchTypeOut }),
          clientPunchID: deps.newClientPunchID(),
        });
      } catch { ok = false; }
    } else if (originalShrunk) {
      try { await restoreOriginalOut(); } catch { ok = false; }
    }
    return ok;
  };
  const fail = (err: unknown, restored: boolean): never => {
    throw new WeekMoveError(err instanceof Error ? err.message : 'The move failed.', { cause: err, restored, partial: !restored });
  };

  if (newInMs > row.inMs) {
    // 1. shrink the original so the new block doesn't overlap it
    try {
      await deps.update(row.id, buildRowEditBody(row.raw, { outMs: newInMs }, zone));
    } catch (err) {
      throw new WeekMoveError(err instanceof Error ? err.message : 'The move failed.', { cause: err, restored: true });
    }
    // 2. add the block in the target week
    let newID: number | null = null;
    try {
      newID = (await deps.add(newBody(outMs))).timeEntryHourlyID ?? null;
    } catch (err) {
      fail(err, await undo(null, false, true));
    }
    // 3. only now delete the original
    try {
      await deps.remove(row.id);
    } catch (err) {
      fail(err, await undo(newID, false, true));
    }
    return;
  }

  // earlier: 1. add the part before the original (touching it)
  let added: { timeEntryHourlyID?: number; timeEntriesHourly?: PortalHourlyRow[] };
  try {
    added = await deps.add(newBody(row.inMs));
  } catch (err) {
    throw new WeekMoveError(err instanceof Error ? err.message : 'The move failed.', { cause: err, restored: true });
  }
  const newID = added.timeEntryHourlyID ?? null;
  // 2. delete the original
  try {
    await deps.remove(row.id);
  } catch (err) {
    fail(err, await undo(newID, false, false));
  }
  // 3. extend the new row to the original OUT
  try {
    if (newID == null) throw new Error("The new punch wasn't confirmed by the server.");
    const raw = added.timeEntriesHourly?.find((r) => r.timeEntryHourlyID === newID) ?? null;
    const body = raw
      ? buildRowEditBody(raw, { outMs }, zone, row.raw.punchTypeOut ?? PUNCH_TYPE_MANUAL_ADD)
      : buildManualRowBody({ employeeID, inMs: newInMs, outMs, zone, type: row.type, job: row.job, punchTypeOut: row.raw.punchTypeOut });
    await deps.update(newID, body);
  } catch (err) {
    fail(err, await undo(newID, true, false));
  }
}

/* ------------------------------------------------------------------ status rail */

export type RailPhase = 'inProgress' | 'open' | 'submitted' | 'supervisorApproved' | 'payrollApproved' | 'exported';

/** Timesheet.ApprovalStatus (API dbInterfaces/Timesheet.ts). */
export const APPROVAL_SUPERVISOR_PENDING = 1;
export const APPROVAL_PAYROLL_PENDING = 2;
export const APPROVAL_PAYROLL_APPROVED = 3;
export const APPROVAL_MANAGER_PENDING = 4;

export function railPhase(status: PortalTimesheetStatus | null, period: Pick<PortalPayPeriod, 'endYmd'>, todayYmd: string): RailPhase {
  if (status?.exported) return 'exported';
  if (status?.submitted) {
    if (status.approvalStatus === APPROVAL_PAYROLL_APPROVED) return 'payrollApproved';
    if (status.approvalStatus === APPROVAL_PAYROLL_PENDING) return 'supervisorApproved';
    return 'submitted';
  }
  return todayYmd > period.endYmd ? 'open' : 'inProgress';
}

export type RailStepState = 'done' | 'wait' | 'todo';

export interface RailStep {
  label: string;
  state: RailStepState;
  /** ISO instant of the step, when it is done and the API knows it. */
  at: string | null;
}

/** Submitted → Supervisor approved → Payroll approved → Exported · locked. */
export function railSteps(status: PortalTimesheetStatus | null): RailStep[] {
  const st = status;
  const done = [
    !!st?.submitted,
    !!st?.submitted && (st.approvalStatus === APPROVAL_PAYROLL_PENDING || st.approvalStatus === APPROVAL_PAYROLL_APPROVED),
    !!st?.submitted && st.approvalStatus === APPROVAL_PAYROLL_APPROVED,
    !!st?.exported,
  ];
  const at = [st?.submittedAt ?? null, st?.supervisorApprovedAt ?? null, st?.payrollApprovedAt ?? null, st?.exportedAt ?? null];
  const labels = ['Submitted', 'Supervisor approved', 'Payroll approved', 'Exported · locked'];
  const waitIndex = st?.exported ? -1 : done.findIndex((d, k) => !d && k > 0 && done[k - 1]);
  return labels.map((label, k) => ({
    label,
    state: done[k] ? 'done' : k === waitIndex ? 'wait' : 'todo',
    at: done[k] ? at[k] : null,
  }));
}

export type PillTone = 'info' | 'ok' | 'warn' | 'neutral';

export interface RailView {
  phase: RailPhase;
  pill: string;
  tone: PillTone;
  sub: string;
  /** Why edits and submit are closed ('exported' wins), else null. */
  lock: 'submitted' | 'exported' | null;
}

export function railView(status: PortalTimesheetStatus | null, period: Pick<PortalPayPeriod, 'endYmd' | 'checkYmd'>, todayYmd: string): RailView {
  const phase = railPhase(status, period, todayYmd);
  const check = period.checkYmd ? fmtYmdDow(period.checkYmd) : null;
  const lock: RailView['lock'] = status?.exported ? 'exported' : status?.submitted || status?.locked ? 'submitted' : null;
  switch (phase) {
    case 'inProgress':
      return { phase, lock, pill: 'In progress', tone: 'info', sub: `Week ends ${fmtYmdDow(period.endYmd)} · submit any time` };
    case 'open':
      return { phase, lock, pill: 'Ready to submit', tone: 'info', sub: `Week ended ${fmtYmdDow(period.endYmd)}` };
    case 'submitted':
      return {
        phase, lock, pill: 'Submitted', tone: 'info',
        sub: status?.approvalStatus === APPROVAL_MANAGER_PENDING
          ? 'Waiting on manager approval'
          : `Waiting on ${status?.supervisorName ?? 'your supervisor'}`,
      };
    case 'supervisorApproved':
      return { phase, lock, pill: 'Supervisor approved', tone: 'info', sub: 'Waiting on payroll' };
    case 'payrollApproved':
      return { phase, lock, pill: 'Payroll approved', tone: 'ok', sub: check ? `Exports for the ${check} check` : 'Waiting on export' };
    case 'exported':
    default:
      return { phase: 'exported', lock: 'exported', pill: 'Exported · locked', tone: 'neutral', sub: check ? `Paid ${check}` : 'Sent to payroll' };
  }
}

export const LOCK_SUBMITTED_MESSAGE =
  "This week was submitted, so punches can't be edited here. Ask your supervisor or payroll to unlock this week.";
export const LOCK_EXPORTED_MESSAGE =
  "Payroll for this week was exported, so punches are locked. Contact payroll if something's wrong.";

/** Why Submit week is disabled right now, else null (any time is fine — no deadline). */
export function submitBlockReason(input: { locked: boolean; loading: boolean; hasOpenPunch: boolean; loadFailed: boolean }): string | null {
  if (input.locked) return 'This week is locked';
  if (input.loading) return 'Loading your week…';
  if (input.loadFailed) return "Your week didn't load — retry first";
  if (input.hasOpenPunch) return 'Clock out first — a punch is still open';
  return null;
}

/* ------------------------------------------------------------------ totals */

export interface DayTotals {
  reg: number;
  ot: number;
  dt: number;
  hol: number;
  sick: number;
  vac: number;
  unpaid: number;
  /** getTimeSummary totalWorkMinutes: worked + sick + vacation (mobile's day total). */
  total: number;
}

export const ZERO_TOTALS: DayTotals = { reg: 0, ot: 0, dt: 0, hol: 0, sick: 0, vac: 0, unpaid: 0, total: 0 };

/** getTimeSummary days → minutes per `YYYY-MM-DD`. */
export function totalsByDay(summary: PortalTimeSummary | null): Map<string, DayTotals> {
  const map = new Map<string, DayTotals>();
  for (const d of summary?.days ?? []) {
    const key = (d.workDate ?? '').slice(0, 10);
    if (!key) continue;
    const prev = map.get(key) ?? ZERO_TOTALS;
    map.set(key, {
      reg: prev.reg + (d.regularMinutes ?? 0),
      ot: prev.ot + (d.overtimeMinutes ?? 0),
      dt: prev.dt + (d.doubleTimeMinutes ?? 0),
      hol: prev.hol + (d.holidayMinutes ?? 0),
      sick: prev.sick + (d.sickMinutes ?? 0),
      vac: prev.vac + (d.vacationMinutes ?? 0),
      unpaid: prev.unpaid + (d.unpaidMinutes ?? 0),
      total: prev.total + (d.totalWorkMinutes ?? 0),
    });
  }
  return map;
}

export function sumTotals(map: Map<string, DayTotals>, ymds: string[]): DayTotals {
  return ymds.reduce<DayTotals>((acc, ymd) => {
    const d = map.get(ymd);
    if (!d) return acc;
    return {
      reg: acc.reg + d.reg, ot: acc.ot + d.ot, dt: acc.dt + d.dt, hol: acc.hol + d.hol,
      sick: acc.sick + d.sick, vac: acc.vac + d.vac, unpaid: acc.unpaid + d.unpaid, total: acc.total + d.total,
    };
  }, ZERO_TOTALS);
}

/** Every row ID of the week (getTimeSummary and submit need the exact set, open rows included). */
export function weekRowIDs(timeEntries: { timeEntryID: number }[], hourly: PortalHourlyRow[]): { timeEntryIDs: number[]; timeEntryHourlyIDs: number[] } {
  return {
    timeEntryIDs: timeEntries.map((e) => e.timeEntryID).filter((id) => Number.isInteger(id)),
    timeEntryHourlyIDs: hourly.map((r) => r.timeEntryHourlyID).filter((id) => Number.isInteger(id)),
  };
}

